import { useMemo, useRef, useState } from "react";
import { IInputProps, Input } from ".";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { ISelectItem, TSelectValue } from "../select";
import clsx from "clsx";

import styles from "./styles.module.scss";
import { useOnClickOutside } from "../../../lib/hooks/useOnClickOutside";
import { searchInputHelper } from "../../../lib/utils";

export type TInputSize = "sm" | "md" | "lg";

interface InputSearchProps extends IInputProps {
  onSelect: (selected: TSelectValue, option: ISelectItem | null) => void;
  length?: number;
  menuArray: ISelectItem[];
  selected: number | null;
  size?: TInputSize;
  minSignSearch?: number;
}

const InputSearch = ({
  onSelect,
  length,
  placeholder,
  menuArray,
  selected,
  size,
  helper,
  minSignSearch = 0,
  error,
  name,
  onReset,
  ...other
}: InputSearchProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [isFocus, setIsFocus] = useState(false);

  const isSearch = minSignSearch ? searchValue.length > minSignSearch : true;

  const isShowError = !!searchValue && !selected && isSearch;

  const filteredOptions = useMemo(() => {
    let list = [...menuArray];

    const search = searchValue.toLowerCase();

    if (searchValue) {
      list = menuArray.filter((item) => {
        const label = (item.label + "").toLowerCase();
        return label.includes(search);
      });

      if (!list.length && searchValue.length > 3) {
        list = searchInputHelper(menuArray, searchValue);
      }
    }

    return list;
  }, [menuArray, searchValue]);

  const handleSelect = (item: ISelectItem) => {
    onSelect(selected === item.key ? 0 : item.key, item);
    setSearchValue(item.label as string);
    setIsFocus(false);
  };

  const handleReset = () => {
    onSelect(null, null);
    setSearchValue("");
    onReset && onReset();
  };

  const inputSearchRef = useRef(null);

  useOnClickOutside(inputSearchRef, () => setIsFocus(false));

  const handleChange = (
    value: string,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLButtonElement>
  ) => {
    if (selected) {
      onSelect(null, null);
    }
    setSearchValue(value);
  };

  return (
    <div style={{ position: "relative" }}>
      <Input
        isMainPlaceholder
        name={"input-search-" + name}
        value={searchValue}
        onChange={handleChange}
        onFocus={() => {
          setIsFocus(true);
        }}
        placeholder={placeholder || "Поиск"}
        helper={isShowError && error ? helper : undefined}
        error={isShowError && error}
        onReset={handleReset}
        {...other}
      />

      {isFocus && isSearch && (
        <div ref={inputSearchRef} className={styles.listContainer}>
          <div className={styles.searchItem}></div>

          <AutoSizer style={{ width: "100%", height: "100%" }}>
            {({ width, height }: { height: number; width: number }) => {
              const currentHeight = filteredOptions.length > 0 ? height : 0;
              return (
                <List
                  innerElementType="ul"
                  itemData={filteredOptions}
                  itemCount={filteredOptions.length}
                  itemSize={60}
                  height={currentHeight}
                  width={width}
                >
                  {({ data, index, style }) => {
                    return (
                      <button
                        style={style}
                        key={data[index].key}
                        type="button"
                        onClick={() => {
                          handleSelect(data[index]);
                        }}
                        data-selected={selected === data[index].key}
                        className={clsx(
                          styles.buttonList,
                          styles[`${size}ButtonListSize`]
                        )}
                      >
                        <div className={styles.textLeft}>
                          <span>{data[index].label}</span>
                          {!!data[index].note && (
                            <small className={styles.textLeft}>
                              {data[index].note}
                            </small>
                          )}
                        </div>
                      </button>
                    );
                  }}
                </List>
              );
            }}
          </AutoSizer>

          {!filteredOptions.length && !searchValue && (
            <div className={styles.empty}>Список пуст</div>
          )}
          {!filteredOptions.length && !!searchValue && (
            <div className={styles.empty}>Ничего не найдено</div>
          )}
        </div>
      )}
    </div>
  );
};

export default InputSearch;
